import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import type { AlertVariant } from "Components/_Shared/Alert";
import { useGetAnalysis, useGetAnalysisQuestionnaireEvaluation } from "Infrastructure/ApiZfp/AnalyzerApi";
import type { ClientDetailResponseClient } from "Infrastructure/ApiZfp/Schema";
import { AnalysisDetailQuestionnaireStatus, UserPermissionsItem } from "Infrastructure/ApiZfp/Schema";
import { useUserContext } from "Infrastructure/Context/UserContext";
import { FeatureFlag, useFeatureFlag } from "Infrastructure/Hooks/useFeatureFlag";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { ClientCardState, getClientCardState } from "Infrastructure/Utils/ClientUtils";
import { useContext } from "react";

export type AlertItem = { key: string; text: string; title?: string; variant?: AlertVariant; ctaTab?: string; ctaHref?: string };

type Props = {
  client?: ClientDetailResponseClient;
};

export const useGoalFulfillmentAlerts = ({ client }: Props) => {
  const { analysisId, versionId } = useContext(AnalysisDetailContext);
  const { t } = useResource();
  const { hasPermission } = useUserContext();
  const isMinorClientEnabled = useFeatureFlag(FeatureFlag.MinorClient);

  const { data: evaluation, isError: isEvaluationError } = useGetAnalysisQuestionnaireEvaluation(analysisId, versionId, {
    query: { retry: 0 },
  });
  const { data: analysis } = useGetAnalysis(analysisId, versionId);
  const isQuestionnaireExpired = analysis?.questionnaireStatus === AnalysisDetailQuestionnaireStatus.expired;

  const alerts: AlertItem[] = [];

  if (!hasPermission(UserPermissionsItem.licensed_investment_advisor)) {
    alerts.push({ key: "no-lia-permission", text: t(Resources.AnalysisDetail.GoalsFulfillment.Alerts.NoLiaPermission) });
  }

  if (isMinorClientEnabled && analysis?.clientLegalRepresentationValid === false) {
    alerts.push({
      key: "adult-with-legal-representative",
      title: t(Resources.Clients.ClientCard.Alerts.AdultWithLegalRepresentative.Title),
      text: t(Resources.Clients.ClientCard.Alerts.AdultWithLegalRepresentative.Text),
      ctaTab: "family",
    });
  }

  if (!client) return { alerts, hasAlerts: alerts.length > 0 };

  switch (getClientCardState(client)) {
    case ClientCardState.NotInMango:
      alerts.push({ key: "not-in-mango", text: t(Resources.Clients.ClientCard.Alerts.NotInMangoUp), ctaTab: "family" });
      break;
    case ClientCardState.Missing:
      alerts.push({ key: "card-missing", text: t(Resources.Clients.ClientCard.Alerts.Missing), ctaTab: "family" });
      break;
    case ClientCardState.Unsigned:
      alerts.push({ key: "unsigned", text: t(Resources.Clients.ClientCard.Alerts.Unsigned), ctaTab: "family" });
      break;
    case ClientCardState.Outdated:
      alerts.push({ key: "outdated", text: t(Resources.Clients.ClientCard.Alerts.Outdated), ctaTab: "family" });
      break;
  }

  if (isEvaluationError) {
    alerts.push({
      key: "eval-not-found",
      text: t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Evaluation.EvaluationNotFound),
      ctaTab: "investment-questionnaire",
    });
  } else if (isQuestionnaireExpired) {
    alerts.push({
      key: "questionnaire-expired",
      text: t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Evaluation.QuestionnaireExpired),
      ctaTab: "investment-questionnaire",
    });
  } else if (evaluation?.signedAt === null) {
    alerts.push({
      key: "questionnaire-unsigned",
      text: t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Evaluation.QuestionnaireNotSigned),
      ctaTab: "investment-questionnaire",
    });
  }

  return { alerts, hasAlerts: alerts.length > 0 };
};
